import type { CSSProperties, ReactNode } from "react";
import { Link } from "react-router-dom";
import PublicHeader from "../../components/PublicHeader";
import PublicFooter from "../../components/PublicFooter";
import {
  ArrowRightIcon,
  BetaBanner,
  Eyebrow,
  Stamp,
} from "../../components/design";
import { BETA_MODE, isTierAvailable, type Tier } from "../../lib/beta";

// /pakete — pricing page with three tiers. In Beta only Basis is selectable.

type Pkg = {
  key: Tier;
  eyebrow: string;
  name: ReactNode;
  price: string;
  tagline: string;
  features: string[];
  featured?: boolean;
};

const PACKAGES: Pkg[] = [
  {
    key: "basis",
    eyebrow: "Basis",
    name: (
      <>
        Das digitale
        <br />
        <em>Erlebnis</em>
      </>
    ),
    price: "149",
    tagline: "Modern, unkompliziert, sofort teilbar.",
    features: [
      "PDF-Biografie · wunderschön gesetzt",
      "Privater Familien-Podcast · sicherer Link",
      "Bis zu 3 Std. Telefon-Interviews",
      "Bis zu 10 Fotos im PDF eingebunden",
    ],
  },
  {
    key: "premium",
    eyebrow: "Bestseller",
    name: "Premium",
    price: "349",
    tagline: "PDF, gesetzt wie ein Verlagsbuch — plus Hörbuch.",
    features: [
      "PDF-Biografie · professionell lektoriert",
      "Hörbuch · professionelle Erzählstimme",
      "Bis zu 6 Std. Telefon-Interviews",
      "Bis zu 25 Fotos im PDF eingebunden",
    ],
    featured: true,
  },
  {
    key: "exklusiv",
    eyebrow: "Exklusiv",
    name: (
      <>
        Das <em>Lebenswerk</em>
      </>
    ),
    price: "699",
    tagline: "Alle Formate — digital, ausführlich, vollständig.",
    features: [
      "Premium-PDF · Lektorat & Bildbearbeitung",
      "Hörbuch + Podcast in allen Formaten",
      "Bis zu 10 Std. Interviews · 50 Fotos",
      "Bevorzugter Kundenservice",
    ],
  },
];

export default function Pakete() {
  return (
    <div className="paper-bg" style={{ minHeight: "100vh" }}>
      <PublicHeader />
      <main
        style={{
          maxWidth: 760,
          margin: "0 auto",
          padding: "12px 22px 30px",
          fontFamily: "var(--f-body)",
          color: "var(--ink)",
        }}
      >
        <section style={{ padding: "32px 0 24px" }}>
          <Eyebrow>Pakete</Eyebrow>
          <h1
            style={{
              fontFamily: "var(--f-display)",
              fontWeight: 400,
              fontSize: 40,
              lineHeight: 1.02,
              letterSpacing: "-0.02em",
              margin: "0 0 14px",
              color: "var(--ink)",
            }}
          >
            Eine Geschichte —
            <br />
            <em>drei Formate</em>.
          </h1>
          <p
            style={{
              fontFamily: "var(--f-body)",
              fontSize: 17,
              lineHeight: 1.45,
              color: "var(--ink-soft)",
              margin: 0,
            }}
          >
            Jedes Paket enthält zwölf wöchentliche Telefongespräche. Was am
            Ende dabei herauskommt, entscheiden Sie.
          </p>
          {BETA_MODE && <BetaBanner />}
        </section>

        <section
          style={{
            padding: "8px 0 24px",
            display: "flex",
            flexDirection: "column",
            gap: 14,
          }}
        >
          {PACKAGES.map((p) => (
            <PricingCard key={p.key} pkg={p} />
          ))}
        </section>

        <section style={{ padding: "12px 0 30px" }}>
          <Eyebrow>Inklusive · bei allen Paketen</Eyebrow>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 10,
            }}
          >
            {[
              "Handgeschriebene Begrüßungskarte",
              "Konrad oder Ida als Biograf*in",
              "Jederzeit Pause möglich",
              "DSGVO · Server in Frankfurt",
            ].map((t) => (
              <div
                key={t}
                style={{
                  background: "var(--paper-50)",
                  border: "1px solid rgba(43,26,15,0.10)",
                  borderRadius: 10,
                  padding: "12px 12px",
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 8,
                }}
              >
                <div
                  aria-hidden
                  style={{
                    fontFamily: "var(--f-display)",
                    color: "var(--ochre)",
                    fontSize: 16,
                    lineHeight: 1,
                  }}
                >
                  ❦
                </div>
                <div
                  style={{
                    fontFamily: "var(--f-body)",
                    fontSize: 13.5,
                    lineHeight: 1.35,
                    color: "var(--ink-soft)",
                  }}
                >
                  {t}
                </div>
              </div>
            ))}
          </div>
        </section>
      </main>
      <PublicFooter />
    </div>
  );
}

function PricingCard({ pkg }: { pkg: Pkg }) {
  const available = isTierAvailable(pkg.key);
  const comingSoon = !available;

  const cardStyle: CSSProperties = {
    background: "var(--paper-50)",
    borderRadius: 16,
    padding: "22px 22px 20px",
    border: pkg.featured
      ? "1.5px solid var(--oxblood)"
      : "1px solid rgba(43,26,15,0.12)",
    boxShadow: pkg.featured
      ? "0 16px 32px -14px rgba(107,32,24,0.18)"
      : "0 8px 16px -10px rgba(43,26,15,0.10)",
    position: "relative",
    opacity: comingSoon ? 0.66 : 1,
  };

  return (
    <div style={cardStyle}>
      {comingSoon && (
        <div style={{ position: "absolute", top: -8, left: 18 }}>
          <Stamp variant="ink" rotation={0}>
            Bald verfügbar
          </Stamp>
        </div>
      )}
      {!comingSoon && pkg.key === "basis" && BETA_MODE && (
        <div style={{ position: "absolute", top: -10, right: 18 }}>
          <Stamp variant="oxblood">Im Beta verfügbar</Stamp>
        </div>
      )}
      {pkg.featured && comingSoon === false && (
        <div style={{ position: "absolute", top: -10, right: 18 }}>
          <Stamp variant="oxblood">{pkg.eyebrow}</Stamp>
        </div>
      )}
      {!pkg.featured && !(pkg.key === "basis" && BETA_MODE) && (
        <Eyebrow style={{ marginBottom: 8 }}>{pkg.eyebrow}</Eyebrow>
      )}
      {pkg.featured && (
        <Eyebrow style={{ marginBottom: 8 }}>{pkg.eyebrow}</Eyebrow>
      )}

      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          justifyContent: "space-between",
          marginBottom: 4,
          gap: 10,
        }}
      >
        <h2
          style={{
            fontFamily: "var(--f-display)",
            fontWeight: 400,
            fontStyle: pkg.featured ? "italic" : "normal",
            fontSize: 32,
            lineHeight: 1,
            margin: 0,
            color: "var(--ink)",
            letterSpacing: "-0.01em",
          }}
        >
          {pkg.name}
        </h2>
        <div style={{ display: "flex", alignItems: "baseline", gap: 2 }}>
          <span
            style={{
              fontFamily: "var(--f-display)",
              fontWeight: 500,
              fontSize: 32,
              color: "var(--oxblood)",
              lineHeight: 1,
            }}
          >
            {pkg.price}
          </span>
          <span
            style={{
              fontFamily: "var(--f-display)",
              fontStyle: "italic",
              fontSize: 16,
              color: "var(--ink-soft)",
            }}
          >
            €
          </span>
        </div>
      </div>
      <div
        style={{
          fontFamily: "var(--f-body)",
          fontStyle: "italic",
          fontSize: 14.5,
          color: "var(--ink-faint)",
          marginBottom: 16,
        }}
      >
        {pkg.tagline}
      </div>

      <ul
        style={{
          listStyle: "none",
          padding: 0,
          margin: "0 0 18px",
          display: "flex",
          flexDirection: "column",
          gap: 7,
        }}
      >
        {pkg.features.map((f) => (
          <li
            key={f}
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: 10,
              fontFamily: "var(--f-body)",
              fontSize: 14.5,
              color: "var(--ink)",
              lineHeight: 1.35,
            }}
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="var(--oxblood)"
              strokeWidth="2.4"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{ marginTop: 3, flexShrink: 0 }}
              aria-hidden
            >
              <polyline points="20 6 9 17 4 12" />
            </svg>
            <span>{f}</span>
          </li>
        ))}
      </ul>

      {comingSoon ? (
        <div
          aria-disabled="true"
          style={{
            width: "100%",
            boxSizing: "border-box",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
            padding: "14px 18px",
            borderRadius: 999,
            background: "rgba(43,26,15,0.04)",
            border: "1px dashed rgba(43,26,15,0.30)",
            fontFamily: "var(--f-ui)",
            fontSize: 14,
            fontWeight: 600,
            color: "var(--ink-faint)",
            cursor: "not-allowed",
          }}
        >
          <svg
            width="13"
            height="13"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden
          >
            <circle cx="12" cy="12" r="9" />
            <path d="M12 7v5l3 2" />
          </svg>
          Ab 1. September 2026 · Benachrichtigen
        </div>
      ) : (
        <Link
          to="/onboarding?step=2"
          className={pkg.featured ? "btn-primary" : "btn-secondary"}
          style={{ width: "100%", boxSizing: "border-box" }}
        >
          {pkg.eyebrow} wählen
          <ArrowRightIcon />
        </Link>
      )}
    </div>
  );
}
